import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ConfirmationDialog, Input, useDialog } from '@gokrew/ui';
import { useCrmFormat } from './crm-format';
import styles from './Crm.module.css';

/**
 * Deletes a CRM record only once the user types the confirmation word
 * (SUPPRIMER / DELETE). Closes the record's editor after success; keeps
 * everything open and shows why on failure.
 */
export const CrmDeleteDialog = ({
  description,
  conflictKey,
  onDelete,
  editorDialogId,
  dialogId,
}: {
  description: string;
  conflictKey: string;
  onDelete: () => Promise<void>;
  editorDialogId?: string;
  dialogId?: string;
}) => {
  const { t } = useCrmFormat();
  const client = useQueryClient();
  const { closeDialog } = useDialog();
  const [typed, setTyped] = useState('');
  const [error, setError] = useState('');
  const word = t('crm.delete_word');

  return (
    <ConfirmationDialog
      dialogId={dialogId}
      waitForSubmit
      variant="destructive"
      confirmLabel={t('crm.delete')}
      confirmDisabled={
        typed.trim().toLocaleUpperCase() !== word.toLocaleUpperCase()
      }
      onSubmit={async () => {
        setError('');
        try {
          await onDelete();
          await Promise.all([
            client.invalidateQueries({ queryKey: ['crm-companies'] }),
            client.invalidateQueries({ queryKey: ['crm'] }),
          ]);
          if (editorDialogId) closeDialog(editorDialogId);
        } catch (err) {
          setError(
            t(
              (err as { response?: { status?: number } }).response?.status ===
                409
                ? conflictKey
                : 'crm.delete_error'
            )
          );
          throw err;
        }
      }}
    >
      <p>{description}</p>
      <label className={styles.deleteConfirm}>
        {t('crm.delete_type', { word })}
        <Input
          autoFocus
          autoComplete="off"
          value={typed}
          placeholder={word}
          onChange={(event) => setTyped(event.target.value)}
        />
      </label>
      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
    </ConfirmationDialog>
  );
};
